import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { Button, Card, CardBody, Col, Container, Input, Row } from "reactstrap";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import dayjs from "dayjs";

import CommonTable from "components/Common/TableContainer";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import Loaders from "components/Common/Loaders";
import {
  AsyncUserSelect,
  StaticFilterSelect,
} from "components/Common/AsyncSearchSelects";
import { DEFAULT_PAGE_SIZE, mapApiMetadataToTableColumnDefinition } from "helpers/GeneralHelper";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";
import {
  applyUserAsyncFilterOptionToFormState,
  buildUserAsyncFilterInitialOption,
  getAsyncFilterLabelFieldKey,
  getUserAsyncFilterLabelFieldKey,
  isAsyncFilterLabelFieldKey,
  isMetadataAsyncSelectFilterDefinition,
  normalizeMetadataFilterDateForApi,
} from "helpers/metadataFilterHelpers";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import { usePermissionContext } from "libs/context/PermissionContext";
import { fetchLeadsAddedTeamOptions } from "model/LeadsAddedReportModel";
import {
  fetchAttendanceMetadata,
  fetchAttendanceStatusOptions,
  resetAttendanceRecords,
  submitAttendanceFilterData,
} from "model/AttendanceModel";

const strOrNull = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed === "" ? null : trimmed;
};

const getDefaultAttendanceDateRange = () => ({
  fromDate: dayjs().subtract(1, "day").format("YYYY-MM-DD"),
  toDate: dayjs().format("YYYY-MM-DD"),
});

const resolveAttendanceToolbarFieldKeys = (metadataFilters = []) => {
  const defaults = (Array.isArray(metadataFilters) ? metadataFilters : []).filter(
    (filterDef) => filterDef?.field && filterDef?.isDefault === true
  );
  const findByField = (fieldName) =>
    defaults.find((filterDef) => filterDef?.field === fieldName);
  const dateFilters = defaults.filter(
    (filterDef) => String(filterDef?.type || "").toLowerCase() === "date"
  );

  const fromDate =
    findByField("fromDate")?.field ||
    findByField("dateFrom")?.field ||
    dateFilters[0]?.field ||
    "fromDate";
  const toDate =
    findByField("toDate")?.field ||
    findByField("dateTo")?.field ||
    dateFilters[1]?.field ||
    "toDate";
  const teamId =
    findByField("teamId")?.field ||
    defaults.find(
      (filterDef) =>
        String(filterDef?.type || "").toLowerCase() === "dropdown" &&
        /team/i.test(
          String(filterDef?.title || filterDef?.placeholder || filterDef?.field || "")
        )
    )?.field ||
    "teamId";
  const userId =
    defaults.find((filterDef) => isMetadataAsyncSelectFilterDefinition(filterDef))
      ?.field || "userId";
  const statusFieldDef = defaults.find(
    (filterDef) =>
      String(filterDef?.type || "").toLowerCase() === "dropdown" &&
      filterDef?.field !== teamId &&
      !isMetadataAsyncSelectFilterDefinition(filterDef)
  );
  const statusId =
    findByField("statusId")?.field ||
    findByField("status")?.field ||
    findByField("attendanceStatus")?.field ||
    statusFieldDef?.field ||
    "statusId";

  return { fromDate, toDate, teamId, userId, statusId, statusFieldDef };
};

const buildInitialAttendanceFilterState = (
  metadataFilters = [],
  fieldKeys = resolveAttendanceToolbarFieldKeys(metadataFilters)
) => {
  const defaultDates = getDefaultAttendanceDateRange();
  const baseline = {};

  (Array.isArray(metadataFilters) ? metadataFilters : []).forEach((filterDef) => {
    const fieldKey = filterDef?.field;
    if (!fieldKey) return;

    if (fieldKey === fieldKeys.fromDate) {
      baseline[fieldKey] = defaultDates.fromDate;
    } else if (fieldKey === fieldKeys.toDate) {
      baseline[fieldKey] = defaultDates.toDate;
    } else if (filterDef?.type === "date") {
      baseline[fieldKey] = null;
    } else {
      baseline[fieldKey] = "";
    }

    if (isMetadataAsyncSelectFilterDefinition(filterDef)) {
      baseline[getAsyncFilterLabelFieldKey(fieldKey)] = "";
    }
  });

  if (!(fieldKeys.statusId in baseline)) {
    baseline[fieldKeys.statusId] = "";
  }

  return baseline;
};

const normalizeAttendanceFilterPayload = (raw = {}, metadataFilters = []) => {
  const outbound = { ...raw };

  (Array.isArray(metadataFilters) ? metadataFilters : []).forEach((filterDef) => {
    const fieldKey = filterDef?.field;
    if (!fieldKey || !(fieldKey in outbound)) return;
    if (filterDef?.type === "date") {
      if (!outbound[fieldKey] && outbound[fieldKey] !== 0) outbound[fieldKey] = null;
      else if (outbound[fieldKey] === "") outbound[fieldKey] = null;
    } else {
      outbound[fieldKey] = strOrNull(outbound[fieldKey]);
    }
  });

  if ("statusId" in outbound) {
    outbound.statusId = strOrNull(outbound.statusId);
  }

  outbound.pageNumber = Number(outbound.pageNumber) || 1;
  outbound.pageSize = Number(outbound.pageSize) || DEFAULT_PAGE_SIZE;

  Object.keys(outbound).forEach((key) => {
    if (isAsyncFilterLabelFieldKey(key)) delete outbound[key];
  });

  delete outbound.status;
  delete outbound.attendanceStatus;

  return outbound;
};

const AttendanceReport = () => {
  const { permissionData } = usePermissionContext() || {};
  const { fetchMetadataSchema } = useCommonFilterOptions();
  const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("AttendanceReport");
  const initialFilterFetchedRef = useRef(false);
  const [metadataReady, setMetadataReady] = useState(false);

  const [attendanceListFilter, setAttendanceListFilter] = useState({});
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });
  const [tableMetaData, setTableMetaData] = useState([]);
  const [attendanceFilterMetaData, setAttendanceFilterMetaData] = useState([]);
  const [teamOptions, setTeamOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  const canResetAttendance = Boolean(permissionData?.UserAttendanceResetAccess);

  const attendanceMetadataToolbarFilterDefinitions = useMemo(
    () =>
      (Array.isArray(attendanceFilterMetaData) ? attendanceFilterMetaData : []).filter(
        (filterDef) => filterDef?.field && filterDef?.isDefault === true
      ),
    [attendanceFilterMetaData]
  );

  const toolbarFieldKeys = useMemo(
    () => resolveAttendanceToolbarFieldKeys(attendanceFilterMetaData),
    [attendanceFilterMetaData]
  );

  const attendanceToolbarFilterDefinitionsByField = useMemo(() => {
    const map = {};
    attendanceMetadataToolbarFilterDefinitions.forEach((filterDef) => {
      if (filterDef?.field) map[filterDef.field] = filterDef;
    });
    return map;
  }, [attendanceMetadataToolbarFilterDefinitions]);

  const mapRecordsToRows = (list = []) =>
    (Array.isArray(list) ? list : []).map((item = {}) => ({
      ...item,
      id: String(
        item?.sessionId ?? item?.id ?? item?._id ?? item?.rowId ?? ""
      ).trim(),
      status: item?.statusLabel ?? item?.status ?? "",
    }));

  const buildPayloadFromFilterForm = useCallback(
    (form = {}, overrides = {}, metadataOverride = null) => {
      const metadataFilters = metadataOverride || attendanceFilterMetaData;
      const fieldKeys = resolveAttendanceToolbarFieldKeys(metadataFilters);
      const metadataValues = {};
      (Array.isArray(metadataFilters) ? metadataFilters : []).forEach((filterDef) => {
        const fieldKey = filterDef?.field;
        if (!fieldKey) return;
        let value = form?.[fieldKey];
        if (filterDef?.type === "date") {
          value = normalizeMetadataFilterDateForApi(value);
        }
        metadataValues[fieldKey] = value;
      });

      const statusFilterValue = form?.[fieldKeys.statusId];
      if (statusFilterValue !== undefined) {
        metadataValues.statusId = statusFilterValue;
      }

      return normalizeAttendanceFilterPayload(
        {
          ...metadataValues,
          pageNumber: 1,
          pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
          ...overrides,
        },
        metadataFilters
      );
    },
    [attendanceFilterMetaData, requestPayload.pageSize]
  );

  const getAttendanceList = async (payloadOverrides = {}) => {
    setLoading(true);
    try {
      const payload = normalizeAttendanceFilterPayload(
        {
          ...requestPayload,
          ...payloadOverrides,
        },
        attendanceFilterMetaData
      );

      const response = await submitAttendanceFilterData(payload);
      const responseData = response?.data ?? response ?? {};
      const listData =
        responseData?.records ??
        responseData?.data ??
        (Array.isArray(responseData) ? responseData : []);
      const records = Array.isArray(listData) ? listData : [];

      if (response == null || response?.success !== false) {
        setAttendanceRows(mapRecordsToRows(records));
        setPaginationMeta({
          pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
          pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
          totalPages: Number(responseData?.totalPages) || 0,
          totalRecords: Number(responseData?.totalRecords) || records.length,
        });

        setRequestPayload(
          normalizeAttendanceFilterPayload(payload, attendanceFilterMetaData)
        );
      } else {
        setAttendanceRows([]);
        toast.error(response?.message || "Unable to load attendance report");
      }
    } catch (error) {
      toast.error(error?.message || "Unable to load attendance report");
      setAttendanceRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadTeamOptions = async () => {
      const teams = await fetchLeadsAddedTeamOptions("AttendanceReport");
      setTeamOptions(Array.isArray(teams) ? teams : []);
    };
    loadTeamOptions();
  }, []);

  useEffect(() => {
    const loadStatusOptions = async () => {
      const options = await fetchAttendanceStatusOptions();
      setStatusOptions(Array.isArray(options) ? options : []);
    };
    loadStatusOptions();
  }, []);

  useEffect(() => {
    if (!Array.isArray(attendanceFilterMetaData) || !attendanceFilterMetaData.length) return;
    setAttendanceListFilter((previous) => {
      const merged = { ...previous };
      let changed = false;
      attendanceFilterMetaData.forEach((filterDef) => {
        const fieldKey = filterDef?.field;
        if (!fieldKey) return;
        if (!(fieldKey in merged)) {
          merged[fieldKey] = filterDef?.type === "date" ? null : "";
          changed = true;
        }
        if (isMetadataAsyncSelectFilterDefinition(filterDef)) {
          const labelKey = getAsyncFilterLabelFieldKey(fieldKey);
          if (!(labelKey in merged)) {
            merged[labelKey] = "";
            changed = true;
          }
        }
      });
      return changed ? merged : previous;
    });
  }, [attendanceFilterMetaData]);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const metaRes = await fetchMetadataSchema("metadata:userAttendance", () =>
          fetchAttendanceMetadata()
        );
        const meta = metaRes?.data ?? metaRes ?? {};
        const metadataFilters = Array.isArray(meta?.filters) ? meta.filters : [];
        const tableColumns = meta?.table?.columns?.length
          ? meta.table.columns.map((col) => mapApiMetadataToTableColumnDefinition(col))
          : [];
        setTableMetaData(tableColumns);
        setAttendanceFilterMetaData(metadataFilters);

        const initialFilterState = buildInitialAttendanceFilterState(metadataFilters);
        setAttendanceListFilter(initialFilterState);
        setMetadataReady(true);
      } catch (error) {
        toast.error(error?.message || "Unable to load attendance metadata");
        setTableMetaData([]);
        setAttendanceFilterMetaData([]);
      }
    };
    bootstrap();
  }, []);

  useEffect(() => {
    if (!metadataReady || !settingsReady || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    getAttendanceList(
      buildPayloadFromFilterForm(
        attendanceListFilter,
        { pageNumber: 1, pageSize },
        attendanceFilterMetaData
      )
    );
  }, [metadataReady, settingsReady, settingsPageSize]);

  const handleUpperToolbarAttendanceFilterSearch = async () => {
    await getAttendanceList(buildPayloadFromFilterForm(attendanceListFilter));
  };

  const handleToolbarDateFilterChange = (event) => {
    const { name, value } = event.target;
    setAttendanceListFilter((previous) => ({
      ...previous,
      [name]: value ?? "",
    }));
  };

  const handleToolbarTeamFilterChange = (selectedOption) => {
    setAttendanceListFilter((previous) => ({
      ...previous,
      [toolbarFieldKeys.teamId]: selectedOption?.value ?? "",
    }));
  };

  const handleToolbarStatusFilterChange = (selectedOption) => {
    setAttendanceListFilter((previous) => ({
      ...previous,
      [toolbarFieldKeys.statusId]: selectedOption?.value ?? "",
    }));
  };

  const handlePageChange = async (page) => {
    await getAttendanceList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getAttendanceList({ pageNumber: 1, pageSize: rows });
  };

  const resolveAttendanceRowId = (row = {}) =>
    String(row?.sessionId ?? row?.id ?? row?._id ?? row?.rowId ?? "").trim();

  const handleResetSelectedAttendance = async (selectedIds = []) => {
    const sessionIds = (Array.isArray(selectedIds) ? selectedIds : [])
      .map((sessionId) => String(sessionId ?? "").trim())
      .filter(Boolean);
    if (!sessionIds.length) {
      toast.error("Select at least one record to reset");
      return false;
    }

    const result = await Swal.fire({
      title: "Reset attendance",
      text: "Are you sure you want to reset selected attendance records?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "OK",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return false;

    const response = await resetAttendanceRecords(sessionIds);
    if (response?.success !== false && (response?.success || response?.isSuccess)) {
      toast.success(response?.message || "Attendance reset successfully");
      await getAttendanceList({});
      return true;
    }
    toast.error(response?.message || "Unable to reset attendance records");
    return false;
  };

  const filteredData = useMemo(() => attendanceRows, [attendanceRows]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h5 className="fw-bold mb-0">Attendance Report</h5>
              </div>
              <hr />

              <LegacyFilterToolbar>
                <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
                  <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--date">
                    <span className="manage-user-filter-label">
                      {attendanceToolbarFilterDefinitionsByField[toolbarFieldKeys.fromDate]
                        ?.title || "From Date"}
                    </span>
                    <div className="metadata-filter-field__control">
                    <Input
                      type="date"
                      name={toolbarFieldKeys.fromDate}
                      className={LEGACY_FILTER_INPUT_CLASS}
                      value={attendanceListFilter?.[toolbarFieldKeys.fromDate] || ""}
                      onChange={handleToolbarDateFilterChange}
                    />
                    </div>
                  </Col>
                  <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--date">
                    <span className="manage-user-filter-label">
                      {attendanceToolbarFilterDefinitionsByField[toolbarFieldKeys.toDate]
                        ?.title || "To Date"}
                    </span>
                    <div className="metadata-filter-field__control">
                    <Input
                      type="date"
                      name={toolbarFieldKeys.toDate}
                      className={LEGACY_FILTER_INPUT_CLASS}
                      value={attendanceListFilter?.[toolbarFieldKeys.toDate] || ""}
                      onChange={handleToolbarDateFilterChange}
                    />
                    </div>
                  </Col>
                  <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--dropdown">
                    <span className="manage-user-filter-label">
                      {attendanceToolbarFilterDefinitionsByField[toolbarFieldKeys.teamId]
                        ?.title || "Team"}
                    </span>
                    <div className="metadata-filter-field__control">
                    <StaticFilterSelect
                      options={teamOptions}
                      placeholder="Select Team"
                      value={attendanceListFilter?.[toolbarFieldKeys.teamId] || ""}
                      onChange={handleToolbarTeamFilterChange}
                    />
                    </div>
                  </Col>
                  <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--autocomplete">
                    <span className="manage-user-filter-label">
                      {attendanceToolbarFilterDefinitionsByField[toolbarFieldKeys.userId]
                        ?.title || "User"}
                    </span>
                    <div className="metadata-filter-field__control">
                    <AsyncUserSelect
                      key={`attendance-user-${attendanceListFilter?.[toolbarFieldKeys.userId] || ""}`}
                      pageName="AttendanceReport"
                      value={attendanceListFilter?.[toolbarFieldKeys.userId] || ""}
                      initialOption={buildUserAsyncFilterInitialOption(
                        attendanceListFilter?.[toolbarFieldKeys.userId],
                        attendanceListFilter[
                          getAsyncFilterLabelFieldKey(toolbarFieldKeys.userId)
                        ] ||
                          attendanceListFilter[getUserAsyncFilterLabelFieldKey()]
                      )}
                      onChange={(option) =>
                        setAttendanceListFilter((previous) =>
                          applyUserAsyncFilterOptionToFormState(previous, option)
                        )
                      }
                      placeholder="Select User"
                    />
                    </div>
                  </Col>
                  <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--dropdown">
                    <span className="manage-user-filter-label">
                      {attendanceToolbarFilterDefinitionsByField[toolbarFieldKeys.statusId]
                        ?.title || "Status"}
                    </span>
                    <div className="metadata-filter-field__control">
                    <StaticFilterSelect
                      options={statusOptions}
                      placeholder="Select Status"
                      value={attendanceListFilter?.[toolbarFieldKeys.statusId] || ""}
                      onChange={handleToolbarStatusFilterChange}
                    />
                    </div>
                  </Col>
                  <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--actions">
                    <span className="manage-user-filter-label" aria-hidden="true">
                      &nbsp;
                    </span>
                    <div className="metadata-filter-toolbar__actions">
                    <Button
                      color="primary"
                      className="w-100 manage-user-search-btn"
                      type="button"
                      onClick={handleUpperToolbarAttendanceFilterSearch}
                    >
                      <i className="fa fa-search me-1" aria-hidden="true" />
                      Search
                    </Button>
                    </div>
                  </Col>
                </Row>
              </LegacyFilterToolbar>

              {loading ? (
                <Loaders />
              ) : tableMetaData?.length ? (
                <CommonTable
                  columns={tableMetaData}
                  data={filteredData}
                  isActionRequired={false}
                  isCheckboxLeftSide={canResetAttendance}
                  enableRowSelection={canResetAttendance}
                  bulkActionLabel="Reset all selected"
                  onBulkAction={handleResetSelectedAttendance}
                  resolveRowId={resolveAttendanceRowId}
                  isPagination={true}
                  currentPage={paginationMeta?.pageNumber}
                  rowsPerPage={paginationMeta?.pageSize}
                  totalPages={paginationMeta?.totalPages}
                  totalRecords={paginationMeta?.totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleRowsPerPageChange}
                  pageName="AttendanceReport"
                />
              ) : (
                <div className="text-muted text-center py-5">
                  Unable to load table columns.
                </div>
              )}
            </CardBody>
          </Card>

        </Container>
      </div>
    </React.Fragment>
  );
};

AttendanceReport.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(AttendanceReport);
